import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
  QueryObserverResult,
  RefetchOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { useMemo } from 'react';

import { Api } from '@/api';
import { DEFAULT_PAGE_NUMBER } from '@/constants';
import {
  INotificationItem,
  NotificationsResponse,
} from '@/types/notification.types';
import { convertKeysToCamelCase } from '@/utils/convertKeysToCamelCase';
import { flattenedData } from '@/utils/getFlattenedData';

type UseFetchNotificationsResponse = {
  notifications: INotificationItem[] | undefined;
  unreadNotifications: number;
  hasNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<NotificationsResponse | undefined>,
      Error
    >
  >;
  refetch: (
    options?: RefetchOptions | undefined,
  ) => Promise<
    QueryObserverResult<
      InfiniteData<NotificationsResponse | undefined, unknown>,
      Error
    >
  >;
} | null;

export const useFetchNotifications = ({
  isAuth,
  enableFetch = false, // Defer fetching until explicitly enabled
}: {
  isAuth: boolean;
  enableFetch?: boolean;
}): UseFetchNotificationsResponse => {
  const {
    data,
    hasNextPage = false,
    fetchNextPage,
    isFetching,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['notification', isAuth] as const,
    queryFn: async ({ pageParam = DEFAULT_PAGE_NUMBER }) => {
      if (!isAuth) return undefined;

      return await Api.notifications.fetchNotifications({
        pageNumber: pageParam,
      });
    },
    initialPageParam: DEFAULT_PAGE_NUMBER,
    getNextPageParam: (lastPage) => {
      if (!lastPage?.hasNextPage) return undefined;
      return lastPage.pageNumber + 1;
    },
    enabled: isAuth && enableFetch, // Only fetch when both auth AND explicitly enabled
    staleTime: 60000, // Cache for 1 min to reduce refetches
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    refetchOnMount: false, // Don't refetch on component remount
  });

  const notifications = useMemo(() => {
    if (!data?.pages.length) return null;

    const dataFiltered = {
      ...data,
      pages: data?.pages.filter((item) => item),
    };

    return flattenedData(dataFiltered).map((item: INotificationItem) => {
      return {
        ...item,
        payload: convertKeysToCamelCase(
          JSON.parse(item.payload as unknown as string),
        ),
      };
    });
  }, [data]);

  return isAuth
    ? {
        notifications,
        unreadNotifications: data?.pages[0]?.totalUnreadCount ?? 0,
        hasNextPage,
        fetchNextPage,
        isFetching,
        refetch,
      }
    : null;
};
